import React, { useEffect, useState } from 'react';
import { getPgQueueApps, getPgQueueTopics } from 'config';
import CountWidget from '../components/CountWidget';
import { t } from 'i18next';
import centralApi from 'services/centralApi';
import { useDiagnosticsAuth } from 'pages/Diagnostics/context/DiagnosticsAuthContext';
import { BOOLEAN_OPTIONS } from 'utils/constants/selectorOptions';
import Spinner from 'components/Spinner/Spinner';
import { API_ENDPOINTS } from 'utils/constants';
import NoData from 'components/NoData/NoData';
import { CONSTANTS } from 'utils/constants/constants';

interface PgQueueApp {
    appid: string;
    name: string;
}

interface PgCountData {
    appid: string;
    name: string;
    topic: string;
    index: number;
    count: number;
}

const PgQueue: React.FC = () => {
    const [loading, setLoading] = useState(BOOLEAN_OPTIONS.FALSE);
    const apps: PgQueueApp[] = getPgQueueApps();
    const topics: string[] = getPgQueueTopics();
    const { token } = useDiagnosticsAuth();
    const [widgets, setWidgets] = useState<PgCountData[]>([]);

    /**
 * Fetches unprocessed PGQueue job counts for app-topic combinations
 * Returns sorted results with correct display indexes
 */
    const fetchCounts = async () => {
        try {
            setLoading(BOOLEAN_OPTIONS.TRUE);
            if (!token) return;

            // Prepare request payload
            const requestPayload = apps.flatMap(app =>
                topics.map(topic => ({ appid: app.appid, topic }))
            );

            const payload = {
                args: JSON.stringify(requestPayload),
                token
            }

            if (requestPayload && requestPayload.length > 0) {
                // Fetch data
                const result = await centralApi('GET', API_ENDPOINTS.DIAGNOSTICS_GET_PGQUEUE_COUNT, null, payload);

                // Transform and sort results
                const transformedResults = result
                     // Filter out items below threshold
                    .filter((item: any) => item.count > CONSTANTS.THRESHOLD)
                    // Sort by count descending
                    .sort((a: any, b: any) => b.count - a.count)
                    // Map to widget format
                    .map((item: any, index: number) => {
                    const name  = apps.find(a => a.appid === item.appid)?.name || ''
                    return {
                            appid: item.appid,
                            name,
                            topic: item.topic,
                            index,
                            count: item.count
                            };
                    });

                setWidgets(transformedResults);
            }
        } catch (error) {
        } finally {
            setLoading(BOOLEAN_OPTIONS.FALSE);
        }
    };

    useEffect(() => {
        let interval: NodeJS.Timeout;
        fetchCounts();
        interval = setInterval(fetchCounts, 30000);
        return () => clearInterval(interval);
    }, []);

    return (
        <>
            <div className="mb-4 pg-queue-container">
                <h6>{t("pgQueueDiagnosticsLabel")}</h6>
                <p className="pg-queue-description-headings">
                    {t("pgQueueDescriptionLabel")}
                </p>
            </div>
            {loading ? (
                <Spinner />
            ) : (
                <div className="diagnostics-widgets-row">
                    {widgets.length === 0 ? (
                        <NoData />
                    ) : (
                        widgets.map((widget) => (
                            <CountWidget
                                key={widget.appid + widget.topic}
                                appid={widget.appid}
                                name={widget.name}
                                topic={widget.topic}
                                index={widget.index}
                                count={widget.count}
                            />
                        ))
                    )}
                </div>
            )}
        </>

    );
};

export default PgQueue;